import React from "react";
import styled from "styled-components";
import { colors } from "app/popleads/components/ui-kit/colors";
import EmptyStatus from "app/popleads/components/ui-kit/EmptyStatus";
import Spinner from "app/popleads/components/ui-kit/Spinner";
import { StyledCardHeader } from "./common/styled";
import moment from "moment";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import useGetLinkedInContactsWithUser from "app/popleads/hooks/useGetLinkedInContactsWithUser";

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    height: 100%;
`;

const StyledTooltip = styled(Tooltip)`
    .tooltip-inner {
        font-size: 11px !important;
    }
`;

const LogContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: rgb(250, 250, 250);
    border-radius: 6px;
    border: 1px solid ${colors.neutral[100]};
    box-shadow: 0px 0px 1px 0px rgba(0, 0, 0, 0.2);
    padding: 8px;
`;

const LogCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    &::after {
        content: "";
        height: 1px;
        background-color: ${colors.neutral[200]};
        display: ${({ isLast }) => (isLast ? "none" : "block")};
    }
`;

const LogContent = styled.div`
    font-size: 14px;
    font-weight: 400;
    letter-spacing: -0.17px;
    line-height: 21px;
    overflow-wrap: anywhere;
`;

const StyledLink = styled.a`
    color: ${colors.l3rnTech[900]};
    text-decoration: none;
    font-weight: 600;
    &:hover {
        color: inherit;
    }
`;

function ExtensionAIAgentFeeds() {
    const { data, loading, error } = useGetLinkedInContactsWithUser();

    return loading ? (
        <LoadingContainer>
            <Spinner size={32} thickness={4} variant="l3rnTech" />
        </LoadingContainer>
    ) : error || !data || data.length === 0 ? (
        <EmptyStatus description="No AI agent feeds available." />
    ) : (
        <LogContainer>
            {data?.map((item, index) => (
                <LogCard key={index} isLast={index === data.length - 1}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <OverlayTrigger placement="top" overlay={<StyledTooltip>Created Date</StyledTooltip>}>
                            <StyledCardHeader>{moment(item.createdDate).format("MMM DD, YYYY HH:mm")}</StyledCardHeader>
                        </OverlayTrigger>
                    </div>
                    <LogContent>
                        I found the <b>{item?.title?.join(", ")}</b> and <b>{item?.country?.join(", ")}</b> details
                        based on your sales data.
                    </LogContent>
                    <LogContent>Using this information, I searched for users on LinkedIn.</LogContent>
                    {item?.profiles?.length > 0 ? (
                        <>
                            <LogContent>
                                I searched and analyzed <b>{item.profiles.length}</b>{" "}
                                {item.profiles.length > 1 ? "users." : "user."}
                            </LogContent>
                            <LogContent>
                                I sent connection requests to{" "}
                                {item.profiles.map((profile, index) => (
                                    <span key={index}>
                                        <StyledLink href={profile.url} target="_blank" rel="noopener noreferrer">
                                            {profile.fullName}
                                        </StyledLink>
                                        {index === item.profiles.length - 1
                                            ? ""
                                            : index === item.profiles.length - 2
                                            ? " and "
                                            : ", "}
                                    </span>
                                ))}
                            </LogContent>
                            {item.profiles
                                .filter((item) => item.status.toLowerCase() === "sent")
                                .map((item, index) => (
                                    <LogContent key={index}>
                                        I recommended the <b>{item.course}</b> course to{" "}
                                        <StyledLink href={item.url} target="_blank" rel="noopener noreferrer">
                                            {item.fullName}
                                        </StyledLink>
                                    </LogContent>
                                ))}
                        </>
                    ) : (
                        <LogContent>
                            I could not find any relevant LinkedIn profiles based on the provided information.
                        </LogContent>
                    )}
                </LogCard>
            ))}
        </LogContainer>
    );
}

export default ExtensionAIAgentFeeds;
